import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Collapse,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  type SelectChangeEvent,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import { useRequestStore, useWorkStore, useAuthStore, useMentorStore, useAttachmentStore } from '../../stores/root_store';
import { useMessageAttachments } from '../../hooks/useMessageAttachments';
import { useTimelineItems } from '../../hooks/useTimelineItems';
import { MessageComposerAttachments } from '../../components/MessageComposerAttachments';
import { REQUEST_STATUS, type PendingResponse, type RequestDelay, type DelayTimelineEvent, type FHRequestMessage } from '../../types';
import { MessageList } from '../../components/MessageList';
import { MessageAttachmentChips } from '../../components/MessageAttachmentChips';
import { VoiceRecordingPlayer } from '../../components/VoiceRecordingPlayer';
import { SafeHtml } from '../../components/SafeHtml';
import { StatusChip } from '../../components/StatusChip';
import { requestApi, type ClientInfo } from '../../api/request_api';
import { fileAssociationApi, type AssociatedFileInfo } from '../../api/file_association_api';
import { PhoneProvider } from '../../contexts/PhoneContext';
import { EmailProvider, useEmail } from '../../contexts/EmailContext';
import type { Email } from '../../api/email_api';
import {
  RequestDetailsCard,
  AttachmentsCard,
  PhoneCallsCard,
  PhoneDialer,
  EmailCard,
  EmailComposer,
  UserNotesCard,
  InternalToolsCard,
  ManagerActionsCard,
  MentorActionsCard,
  MentorHistoryCard,
  CalendarCard,
  FieldGuideSuggestionsCard,
} from '../../components/sidebar';
import { canWorkOnRequest } from '../../utils/permissions';
import { managerApi } from '../../api/manager_api';
import { aiApi, type AISuggestion as AISuggestionType } from '../../api/ai_api';
import { RecurringTaskBanner } from '../../components/RecurringTaskBanner';
import { DelayExpirationBanner } from '../../components/DelayExpirationBanner';

// localStorage helpers for draft persistence
const DRAFT_STORAGE_PREFIX = 'fh_draft_';

interface DraftData {
  message: string;
  action: MessageAction;
}

const getDraftKey = (requestKey: string) => `${DRAFT_STORAGE_PREFIX}${requestKey}`;

const loadDraft = (requestKey: string): DraftData | null => {
  try {
    const data = localStorage.getItem(getDraftKey(requestKey));
    if (data) {
      return JSON.parse(data);
    }
  } catch (e) {
    console.error('Failed to load draft:', e);
  }
  return null;
};

const saveDraft = (requestKey: string, message: string, action: MessageAction) => {
  try {
    if (message.trim()) {
      localStorage.setItem(getDraftKey(requestKey), JSON.stringify({ message, action }));
    } else {
      // Clear draft if message is empty
      localStorage.removeItem(getDraftKey(requestKey));
    }
  } catch (e) {
    console.error('Failed to save draft:', e);
  }
};

const clearDraft = (requestKey: string) => {
  try {
    localStorage.removeItem(getDraftKey(requestKey));
  } catch (e) {
    console.error('Failed to clear draft:', e);
  }
};

// Message actions for the dropdown - these represent the suggested status after mentor approval
type MessageAction = 'open' | 'close' | 'waiting' | 'awaiting';

// Inner component that uses the contexts
const AssistantRequestDetailInner = observer(() => {
  const { requestKey } = useParams<{ requestKey: string }>();
  const navigate = useNavigate();
  const requestStore = useRequestStore();
  const workStore = useWorkStore();
  const authStore = useAuthStore();
  const mentorStore = useMentorStore();
  const attachmentStore = useAttachmentStore();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Attachment handling
  const {
    attachments,
    addFiles,
    removeAttachment,
    clearAttachments,
    uploading,
    associateAll,
  } = useMessageAttachments();

  const [newMessage, setNewMessage] = useState('');
  const [messageAction, setMessageAction] = useState<MessageAction>('open');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(() => !isMobile);
  const [clientInfo, setClientInfo] = useState<ClientInfo | null>(null);
  const [clientInfoLoading, setClientInfoLoading] = useState(false);
  const [phoneDialerOpen, setPhoneDialerOpen] = useState(false);
  const [dialerInitialNumber, setDialerInitialNumber] = useState<string | undefined>();
  const [emailComposerOpen, setEmailComposerOpen] = useState(false);
  const [replyToEmail, setReplyToEmail] = useState<Email | undefined>(undefined);
  const [replyToIncomingEmail, setReplyToIncomingEmail] = useState<{ senderEmail: string; subject?: string } | undefined>(undefined);
  const [recalling, setRecalling] = useState(false);
  const [claimError, setClaimError] = useState<string | null>(null);
  const [pendingResponse, setPendingResponse] = useState<PendingResponse | null>(null);
  const [pendingResponseLoading, setPendingResponseLoading] = useState(false);
  const [pendingResponseError, setPendingResponseError] = useState(false);
  const [needHelpOpen, setNeedHelpOpen] = useState(false);
  const [needHelpMessage, setNeedHelpMessage] = useState('');
  const [needHelpSending, setNeedHelpSending] = useState(false);
  // Manager response state
  const [managerRespondOpen, setManagerRespondOpen] = useState(false);
  const [managerRespondMessage, setManagerRespondMessage] = useState('');
  const [managerRespondStatus, setManagerRespondStatus] = useState<number>(REQUEST_STATUS.OPEN);
  const [managerRespondSending, setManagerRespondSending] = useState(false);
  const [messageAttachments, setMessageAttachments] = useState<Record<string, AssociatedFileInfo[]>>({});
  const [requestAttachments, setRequestAttachments] = useState<AssociatedFileInfo[]>([]);
  // AI Suggest state
  const [aiSuggestion, setAiSuggestion] = useState<AISuggestionType | null>(null);
  // Delay picker state
  const [delayTime, setDelayTime] = useState<string>('');
  // Close task state
  const [closeTaskOpen, setCloseTaskOpen] = useState(false);
  const [closeTaskSending, setCloseTaskSending] = useState(false);
  // Release task dialog state
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [releasing, setReleasing] = useState(false);
  // Delay history for timeline
  const [delayEvents, setDelayEvents] = useState<DelayTimelineEvent[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Validation for delay time when "waiting" action is selected
  const delayTimeError = useMemo(() => {
    if (messageAction !== 'waiting') return '';
    if (!delayTime) return 'Time is required';
    try {
      const parsed = new Date(delayTime);
      if (isNaN(parsed.getTime())) return 'Invalid date';
      if (parsed <= new Date()) return 'Time must be in the future';
    } catch {
      return 'Invalid date';
    }
    return '';
  }, [messageAction, delayTime]);

  // Quick-select delay values
  const delayQuickSelects = useMemo(() => {
    const fmt = (d: Date) => {
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    const now = new Date();
    const oneHour = new Date(now.getTime() + 60 * 60 * 1000);
    const tomorrow9AM = new Date(now);
    tomorrow9AM.setDate(tomorrow9AM.getDate() + 1);
    tomorrow9AM.setHours(9, 0, 0, 0);
    const oneMinute = new Date(now.getTime() + 60 * 1000);
    return { oneHour: fmt(oneHour), tomorrow9AM: fmt(tomorrow9AM), oneMinute: fmt(oneMinute) };
  }, []);

  // Convert RequestDelay to timeline events
  const delayToTimelineEvents = useCallback((delay: RequestDelay): DelayTimelineEvent[] => {
    const events: DelayTimelineEvent[] = [];

    // Always have a "scheduled" event from date_created
    events.push({
      uuid: `${delay.uuid}-scheduled`,
      event_type: 'scheduled',
      date: delay.date_created,
      scheduled_time: delay.scheduled_time,
      delay_uuid: delay.uuid,
    });

    if (delay.fired_date) {
      events.push({
        uuid: `${delay.uuid}-fired`,
        event_type: 'fired',
        date: delay.fired_date,
        delay_uuid: delay.uuid,
      });
    }
    if (delay.canceled_date) {
      events.push({
        uuid: `${delay.uuid}-canceled`,
        event_type: 'canceled',
        date: delay.canceled_date,
        delay_uuid: delay.uuid,
      });
    }

    return events;
  }, []);

  const { getEmailsByAssociation, fetchEmailsByAssociation } = useEmail();

  const request = requestKey ? requestStore.getRequestByKey(requestKey) : undefined;
  const messages = request?.key ? requestStore.messages.get(request.key) || [] : [];
  const emails = request?.uuid ? getEmailsByAssociation('request', request.uuid) : [];
  const timelineItems = useTimelineItems(messages, emails, 'assistant', delayEvents);

  // Fetch emails when request loads
  useEffect(() => {
    if (request?.uuid) {
      fetchEmailsByAssociation('request', request.uuid);
    }
  }, [request?.uuid, fetchEmailsByAssociation]);

  // Fetch delay history when request loads
  useEffect(() => {
    const fetchDelayHistory = async () => {
      if (!request?.uuid) return;
      try {
        const delays = await requestApi.getDelayHistory(request.uuid);
        const events = delays.flatMap(delayToTimelineEvents);
        setDelayEvents(events);
      } catch (err) {
        console.error('Failed to fetch delay history:', err);
      }
    };
    fetchDelayHistory();
  }, [request?.uuid, delayToTimelineEvents]);

  // Scroll to bottom when messages change
  useEffect(() => {
    const timer = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
    return () => clearTimeout(timer);
  }, [messages.length, requestKey]);

  // Update sidebar visibility when screen size changes
  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    const loadRequest = async () => {
      if (requestKey) {
        setLoading(true);
        await requestStore.fetchRequest(requestKey);
        setLoading(false);
      }
    };
    loadRequest();
  }, [requestKey, requestStore]);

  useEffect(() => {
    if (request?.key) {
      requestStore.fetchMessages(request.key, true);
    }
  }, [request?.key, requestStore]);

  // Fetch message attachments when messages change
  useEffect(() => {
    const fetchMessageAttachments = async () => {
      if (messages.length === 0) return;

      const messageUuids = messages.map(m => m.uuid).filter(Boolean);
      if (messageUuids.length === 0) return;

      try {
        const result = await fileAssociationApi.listByAssociations('request_message', messageUuids);
        setMessageAttachments(result.items);
      } catch (err) {
        console.error('Failed to fetch message attachments:', err);
      }
    };
    fetchMessageAttachments();
  }, [messages]);

  // Fetch request-level attachments (for initial email content)
  useEffect(() => {
    const fetchRequestAttachments = async () => {
      if (!request?.uuid) return;

      try {
        const result = await fileAssociationApi.listByAssociations('request', [request.uuid]);
        const allRequestAttachments = result.items[request.uuid] || [];

        // De-duplicate: exclude attachments that are already shown with messages
        const messageFileUuids = new Set(
          Object.values(messageAttachments).flat().map(a => a.file_uuid)
        );
        const filtered = allRequestAttachments.filter(
          a => !messageFileUuids.has(a.file_uuid)
        );
        setRequestAttachments(filtered);
      } catch (err) {
        console.error('Failed to fetch request attachments:', err);
      }
    };
    fetchRequestAttachments();
  }, [request?.uuid, messageAttachments]);

  // Load draft from localStorage or set default action when request loads
  useEffect(() => {
    if (requestKey && request?.status !== undefined) {
      const draft = loadDraft(requestKey);
      const validActions = getMessageActions(request.status);

      if (draft) {
        // Restore draft message
        setNewMessage(draft.message);
        // Only set the action if it's valid for the current request status
        if (validActions.some(a => a.value === draft.action)) {
          setMessageAction(draft.action);
        } else if (validActions.length > 0) {
          // Fall back to default if saved action is no longer valid
          const defaultAction = request.status >= REQUEST_STATUS.CLOSED ? 'close' : 'open';
          setMessageAction(defaultAction);
        }
      } else if (validActions.length > 0) {
        // No draft - set default action based on request status
        const defaultAction = request.status >= REQUEST_STATUS.CLOSED ? 'close' : 'open';
        setMessageAction(defaultAction);
      }
    }
  }, [requestKey, request?.status]);

  // Save draft to localStorage when message or action changes (debounced)
  // Only save when there are valid actions (i.e., not awaiting mentor approval)
  const saveDraftDebounced = useCallback((message: string, action: MessageAction, status: number | undefined) => {
    if (requestKey && getMessageActions(status).length > 0) {
      saveDraft(requestKey, message, action);
    }
  }, [requestKey]);

  useEffect(() => {
    const timer = setTimeout(() => {
      saveDraftDebounced(newMessage, messageAction, request?.status);
    }, 500); // 500ms debounce
    return () => clearTimeout(timer);
  }, [newMessage, messageAction, request?.status, saveDraftDebounced]);

  // Fetch client info when request loads
  useEffect(() => {
    if (!requestKey) return;
    let cancelled = false;
    const loadClientInfo = async () => {
      setClientInfo(null);
      setClientInfoLoading(true);
      try {
        const info = await requestApi.getClientInfo(requestKey);
        if (!cancelled) setClientInfo(info);
      } catch (err) {
        console.error('Failed to load client info:', err);
      } finally {
        if (!cancelled) setClientInfoLoading(false);
      }
    };
    loadClientInfo();
    return () => { cancelled = true; };
  }, [requestKey]);

  // Reset pending response error state when request changes
  useEffect(() => {
    setPendingResponseError(false);
    setPendingResponse(null);
  }, [requestKey]);

  // Fetch pending response when awaiting mentor approval or when denied (needs revision)
  useEffect(() => {
    const loadPendingResponse = async () => {
      if (
        requestKey &&
        (request?.status === REQUEST_STATUS.AWAITING_MENTOR_APPROVAL ||
         request?.status === REQUEST_STATUS.MENTOR_RESPONSE) &&
        request.assistant_uuid === authStore.user?.uuid &&
        !pendingResponse &&
        !pendingResponseLoading &&
        !pendingResponseError
      ) {
        setPendingResponseLoading(true);
        try {
          const response = await requestApi.getPendingResponse(requestKey);
          setPendingResponse(response);
        } catch (err) {
          console.error('Failed to load pending response:', err);
          setPendingResponseError(true);
        } finally {
          setPendingResponseLoading(false);
        }
      }
    };
    loadPendingResponse();
  }, [requestKey, request?.status, request?.claimed_by, authStore.user?.key, pendingResponse, pendingResponseLoading, pendingResponseError]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !requestKey || !authStore.isAssistant || !request) return;
    if (uploading) return; // Don't send while uploads in progress
    if (messageAction === 'waiting' && delayTimeError) return; // Don't send with invalid delay time

    setSending(true);
    setError(null);

    try {
      // Get the suggested status from the selected action
      const actions = getMessageActions(request?.status);
      const selectedAction = actions.find(a => a.value === messageAction);
      const suggestedStatus = selectedAction?.suggestedStatus ?? REQUEST_STATUS.CLOSED;

      // Submit response for mentor approval (response is hidden from client until approved)
      const result = await requestApi.submitResponse(
        requestKey,
        newMessage.trim(),
        suggestedStatus,
        messageAction === 'waiting' && delayTime ? new Date(delayTime).toISOString() : undefined
      );

      // Associate any uploaded files with the response
      // (backend will copy to request_message and request on approval)
      if (attachments.length > 0) {
        const responseData = result.response as { uuid: string };
        if (responseData?.uuid) {
          await associateAll([
            { type: 'response', uuid: responseData.uuid },
          ]);
          // Refresh attachments sidebar after approval copies to request
          attachmentStore.fetchByRequest(requestKey);
        }
      }

      // Refresh request to get updated status
      await requestStore.fetchRequest(requestKey);

      // Track AI suggestion usage if one was active
      if (aiSuggestion) {
        const sent = newMessage.trim();
        const original = aiSuggestion.content.trim();
        if (sent === original) {
          aiApi.trackSuggestion(aiSuggestion.uuid, 'accepted');
        } else {
          const editDist = 1 - (Math.min(sent.length, original.length) / Math.max(sent.length, original.length, 1));
          aiApi.trackSuggestion(aiSuggestion.uuid, 'edited', editDist);
        }
        setAiSuggestion(null);
      }

      setNewMessage('');
      setMessageAction('close'); // Reset to default
      setDelayTime('');
      clearDraft(requestKey); // Clear saved draft after successful send
      clearAttachments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit response');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    // Ctrl+Enter or Cmd+Enter to send
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleClaim = async () => {
    if (!requestKey) return;
    setClaimError(null);
    const claimedRequest = await workStore.claimRequest(requestKey);
    if (!claimedRequest) {
      setClaimError(workStore.error);
    } else {
      // Refresh the request to get updated state
      await requestStore.fetchRequest(requestKey);
    }
  };

  const handleRecall = async () => {
    if (!requestKey) return;
    setRecalling(true);
    setError(null);
    try {
      const recallResponse = await mentorStore.recallResponse(requestKey);
      // Clear the pending response state so it's not shown after recall
      setPendingResponse(null);

      // Pre-populate the text field with the recalled response body
      if (recallResponse?.recalled_response) {
        setNewMessage(recallResponse.recalled_response);
      }

      // Restore delay time from recalled response
      if (recallResponse?.recalled_delay_time) {
        setDelayTime(recallResponse.recalled_delay_time);
      }

      // Set the message action to match the recalled suggested status
      if (recallResponse?.recalled_suggested_status !== null && recallResponse?.recalled_suggested_status !== undefined) {
        const statusToAction: Record<number, MessageAction> = {
          [REQUEST_STATUS.OPEN]: 'open',
          [REQUEST_STATUS.CLOSED]: 'close',
          [REQUEST_STATUS.AWAITING_THIRD_PARTY]: 'waiting',
        };
        const action = statusToAction[recallResponse.recalled_suggested_status];
        if (action) {
          setMessageAction(action);
        }
      }

      // Refresh the request to get updated status
      await requestStore.fetchRequest(requestKey);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to recall response');
    } finally {
      setRecalling(false);
    }
  };

  const handleRequestHelp = async () => {
    if (!requestKey || !needHelpMessage.trim()) return;
    setNeedHelpSending(true);
    setError(null);
    try {
      await requestApi.requestHelp(requestKey, needHelpMessage.trim());
      setNeedHelpMessage('');
      setNeedHelpOpen(false);
      // Refresh request to get updated needs_manager_attention flag
      await requestStore.fetchRequest(requestKey);
      // Refresh messages to show the new note
      await requestStore.fetchMessages(requestKey, true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to request help');
    } finally {
      setNeedHelpSending(false);
    }
  };

  const handleCancelHelp = async () => {
    if (!requestKey) return;
    setError(null);
    try {
      await requestApi.cancelHelp(requestKey);
      await requestStore.fetchRequest(requestKey);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel help request');
    }
  };

  // Manager response handlers
  const handleManagerRespond = async () => {
    if (!requestKey || !managerRespondMessage.trim()) return;
    setManagerRespondSending(true);
    setError(null);
    try {
      await managerApi.respondToRequest(requestKey, managerRespondMessage.trim(), managerRespondStatus);
      setManagerRespondMessage('');
      setManagerRespondStatus(REQUEST_STATUS.OPEN);
      setManagerRespondOpen(false);
      await requestStore.fetchRequest(requestKey);
      await requestStore.fetchMessages(requestKey, true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send response');
    } finally {
      setManagerRespondSending(false);
    }
  };

  // Close task handler - closes without sending a response to the client
  const handleCloseTask = async () => {
    if (!requestKey) return;
    setCloseTaskSending(true);
    setError(null);
    try {
      await requestApi.closeTask(requestKey);
      setCloseTaskOpen(false);
      // Refresh request to get updated status
      await requestStore.fetchRequest(requestKey);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to close task');
    } finally {
      setCloseTaskSending(false);
    }
  };

  // Release task handler - unclaims the task so it goes back to Available
  const handleRelease = async () => {
    if (!requestKey) return;
    setReleasing(true);
    setError(null);
    try {
      await workStore.releaseRequest(requestKey);
      setReleaseOpen(false);
      navigate('/assistant/mine');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to release task');
      setReleaseOpen(false);
    } finally {
      setReleasing(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!request) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">Request not found</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/assistant')}
          sx={{ mt: 2 }}
        >
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  const isOpen = request.status < REQUEST_STATUS.CLOSED;

  return (
    <Box sx={{ display: 'flex', py: 4, px: 2, gap: 2 }}>
      {/* Main Content */}
      <Box sx={{ flex: 1, maxWidth: sidebarOpen ? 'calc(100% - 340px)' : '100%' }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/assistant')}
          sx={{ mb: 2 }}
        >
          Back to Dashboard
        </Button>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                mb: 2,
              }}
            >
              <Typography variant="h5" component="h1">
                {request.title || 'Untitled Request'}
              </Typography>
              <StatusChip status={request.status} size="medium" />
            </Box>

            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Created: {new Date(request.date_created).toLocaleString()}
            </Typography>

            {/* Display internal manager note */}
            {request.god_note && (
              <Alert
                severity="info"
                sx={{
                  mb: 2,
                  '& .MuiAlert-message': { fontStyle: 'italic' },
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                  Internal note from manager:
                </Typography>
                "{request.god_note}"
              </Alert>
            )}

            <Divider sx={{ my: 2 }} />

            {request.content_type === 'text/html' ? (
              <SafeHtml content={request.content} forceLight />
            ) : (
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {request.content}
              </Typography>
            )}

            {request.is_voice_request && (
              <VoiceRecordingPlayer attachments={requestAttachments} />
            )}

            {requestAttachments.filter(a => !a.content_type?.startsWith('audio/')).length > 0 && (
              <MessageAttachmentChips
                attachments={requestAttachments.filter(a => !a.content_type?.startsWith('audio/'))}
              />
            )}
          </CardContent>
        </Card>

        <RecurringTaskBanner request={request} />
        <DelayExpirationBanner requestUuid={request.uuid} />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Messages Section */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Messages
            </Typography>

            {requestStore.messagesLoading && messages.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : messages.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 2 }}>
                No messages yet.
              </Typography>
            ) : (
              <>
                <MessageList
                  items={timelineItems}
                  perspective="assistant"
                  attachmentsByMessage={messageAttachments}
                  onReplyToIncomingEmail={(message: FHRequestMessage) => {
                    // Extract sender email from the message
                    // The sender field contains "Name <email>" format, extract just the email
                    const senderMatch = message.sender?.match(/<([^>]+)>/) || message.sender?.match(/([^\s]+@[^\s]+)/);
                    const senderEmail = senderMatch ? senderMatch[1] : message.sender || '';
                    setReplyToIncomingEmail({
                      senderEmail,
                      subject: message.subject || undefined,
                    });
                    setReplyToEmail(undefined);
                    setEmailComposerOpen(true);
                  }}
                />
                <div ref={messagesEndRef} />
              </>
            )}

            {/* Only show message form if user can work AND there are actions available */}
            {isOpen && canWorkOnRequest(request, authStore.user?.uuid, authStore.isAssistant) &&
             getMessageActions(request.status).length > 0 && (
              <Box sx={{ mt: 2 }}>
                {/* AI Suggest - hidden for now, enable when ready
                {!newMessage.trim() && (
                  <Box sx={{ mb: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleAiSuggest}
                      disabled={aiSuggesting || sending}
                      startIcon={aiSuggesting ? <CircularProgress size={14} /> : <AutoAwesomeIcon />}
                      sx={{ textTransform: 'none' }}
                    >
                      {aiSuggesting ? 'Generating...' : 'AI Suggest'}
                    </Button>
                    {aiSuggestionError && (
                      <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                        {aiSuggestionError}
                      </Typography>
                    )}
                  </Box>
                )}
                {aiSuggestion && newMessage.trim() && (
                  <Alert severity="info" sx={{ mb: 1 }} onClose={() => {
                    aiApi.trackSuggestion(aiSuggestion.uuid, 'discarded');
                    setAiSuggestion(null);
                  }}>
                    AI-generated draft loaded. Edit as needed.
                  </Alert>
                )}
                */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      placeholder="Type a message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      multiline
                      minRows={3}
                      maxRows={10}
                      disabled={sending}
                    />
                    <MessageComposerAttachments
                      attachments={attachments}
                      onAddFiles={addFiles}
                      onRemove={removeAttachment}
                      disabled={sending}
                    />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center', justifyContent: 'space-between' }}>
                  {/* Close Without Responding / Release - visible when assistant owns the task */}
                  {request.assistant_uuid === authStore.user?.uuid && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        color="warning"
                        size="small"
                        onClick={() => setCloseTaskOpen(true)}
                        startIcon={<CloseIcon />}
                      >
                        Close Without Responding
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setReleaseOpen(true)}
                        startIcon={<AssignmentReturnIcon />}
                      >
                        Release Task
                      </Button>
                    </Box>
                  )}
                  {request.assistant_uuid !== authStore.user?.uuid && <Box />}
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <Select
                        value={messageAction}
                        onChange={(e: SelectChangeEvent) =>
                          setMessageAction(e.target.value as MessageAction)
                        }
                        disabled={sending}
                      >
                        {getMessageActions(request.status).map((action) => (
                          <MenuItem key={action.value} value={action.value}>
                            {action.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="contained"
                      color={messageAction === 'close' ? 'success' : 'primary'}
                      onClick={handleSendMessage}
                      disabled={sending || uploading || !newMessage.trim() || (messageAction === 'waiting' && !!delayTimeError)}
                      startIcon={
                        (sending || uploading) ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : messageAction === 'close' ? (
                          <CheckCircleIcon />
                        ) : (
                          <SendIcon />
                        )
                      }
                    >
                      {getMessageActions(request.status).find((a) => a.value === messageAction)?.label || 'Submit'}
                    </Button>
                  </Box>
                </Box>
                {/* Delay picker - shown when "Waiting on third party" is selected */}
                {messageAction === 'waiting' && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      You'll get a reminder when the delay fires and the task will be ready to work on.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <TextField
                        type="datetime-local"
                        size="small"
                        value={delayTime}
                        onChange={(e) => setDelayTime(e.target.value)}
                        disabled={sending}
                        error={!!delayTimeError}
                        helperText={delayTimeError}
                        sx={{ minWidth: 220 }}
                        slotProps={{
                          inputLabel: { shrink: true },
                          htmlInput: { min: new Date().toISOString().slice(0, 16) },
                        }}
                      />
                      <Chip
                        label="One Hour"
                        onClick={() => setDelayTime(delayQuickSelects.oneHour)}
                        color={delayTime === delayQuickSelects.oneHour ? 'primary' : 'default'}
                        variant={delayTime === delayQuickSelects.oneHour ? 'filled' : 'outlined'}
                      />
                      <Chip
                        label="9am Tomorrow"
                        onClick={() => setDelayTime(delayQuickSelects.tomorrow9AM)}
                        color={delayTime === delayQuickSelects.tomorrow9AM ? 'primary' : 'default'}
                        variant={delayTime === delayQuickSelects.tomorrow9AM ? 'filled' : 'outlined'}
                      />
                      {window.location.hostname === 'localhost' && (
                        <Chip
                          label="1 minute"
                          onClick={() => setDelayTime(delayQuickSelects.oneMinute)}
                          color={delayTime === delayQuickSelects.oneMinute ? 'primary' : 'default'}
                          variant={delayTime === delayQuickSelects.oneMinute ? 'filled' : 'outlined'}
                        />
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {/* Show alert when mentor denied and task needs revision */}
            {request.status === REQUEST_STATUS.MENTOR_RESPONSE &&
             request.assistant_uuid === authStore.user?.uuid && (
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    A mentor has requested revisions to your response. Please review and resubmit.
                  </Alert>

                  {/* Show denied response content */}
                  {pendingResponseLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : pendingResponse ? (
                    <Box
                      sx={{
                        bgcolor: 'action.hover',
                        borderLeft: 4,
                        borderColor: 'warning.main',
                        p: 2,
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Your original response:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: 'pre-wrap' }}
                      >
                        {pendingResponse.body}
                      </Typography>
                    </Box>
                  ) : null}
                </CardContent>
              </Card>
            )}

            {/* Show recall option when awaiting mentor approval - only for the assistant who submitted */}
            {request.status === REQUEST_STATUS.AWAITING_MENTOR_APPROVAL &&
             request.assistant_uuid === authStore.user?.uuid &&
             !request.mentor_claimed && (
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Awaiting Mentor Review
                  </Typography>

                  {/* Show pending response content */}
                  {pendingResponseLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : pendingResponse ? (
                    <Box
                      sx={{
                        bgcolor: 'action.hover',
                        borderLeft: 4,
                        borderColor: 'primary.main',
                        p: 2,
                        mb: 2,
                        borderRadius: 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Status if approved: <strong>{getStatusLabel(pendingResponse.suggested_status)}</strong>
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: 'pre-wrap', fontStyle: 'italic' }}
                      >
                        {pendingResponse.body}
                      </Typography>
                      {pendingResponse.author_name && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          — {pendingResponse.author_name}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Your response is awaiting mentor review.
                    </Typography>
                  )}

                  <Box sx={{ textAlign: 'center' }}>
                    <Button
                      variant="outlined"
                      startIcon={recalling ? <CircularProgress size={16} /> : <EditIcon />}
                      onClick={handleRecall}
                      disabled={recalling || !authStore.isAssistant}
                    >
                      {recalling ? 'Recalling...' : 'Edit Response'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Show simple alert when awaiting mentor approval but can't recall (mentor claimed it or not the assistant) */}
            {request.status === REQUEST_STATUS.AWAITING_MENTOR_APPROVAL &&
             (request.assistant_uuid !== authStore.user?.uuid || request.mentor_claimed) &&
             !authStore.isMentor && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Your response is awaiting mentor review.
              </Alert>
            )}

            {!isOpen && (
              <Alert severity="success" sx={{ mt: 2 }}>
                This request has been closed.
              </Alert>
            )}

            {isOpen && !authStore.isAssistant && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Only assistants can send messages.
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Claim Button - show for unclaimed requests when user is an assistant */}
        {!request.claimed && authStore.isAssistant && (
          <Card sx={{ mt: 2 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              {claimError ? (
                <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
                  {claimError}
                </Alert>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  This request is available to claim
                </Typography>
              )}
              <Button
                variant="contained"
                size="large"
                onClick={handleClaim}
              >
                Claim Request
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Mentor Actions Card - show in main content for mentors viewing awaiting approval */}
        {authStore.isMentor && authStore.isAssistant &&
         request.status === REQUEST_STATUS.AWAITING_MENTOR_APPROVAL && (
          <Box sx={{ mt: 2 }}>
            <MentorActionsCard
              request={request}
              currentUserUuid={authStore.user?.uuid || ''}
              onRequestUpdate={() => requestStore.fetchRequest(requestKey!)}
            />
          </Box>
        )}

        {/* Manager Attention Section - show for managers when request needs attention */}
        {request.needs_manager_attention &&
         authStore.roles.includes('manager') &&
         authStore.isAssistant && (() => {
          // Find the most recent help request from the assistant.
          // Messages are sorted oldest-first (request_store.sortMessages), so the
          // LAST matching message is the most recent one. Match only the assistant's
          // OWN messages (author === 'user'): message_type === 'assistant' also covers
          // the manager's own notes (author === 'manager', via /request/manager/respond)
          // and legacy internal notes, which must NOT be highlighted as the question to
          // answer — otherwise an unrelated, often older note gets shown instead of the
          // assistant's actual help request.
          const helpRequestMessage =
            [...messages]
              .reverse()
              .find(m => m.message_type === 'assistant' && m.author === 'user') || null;

          return (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">
                    This request needs manager attention
                  </Typography>
                  {request.assistant_name && (
                    <Typography variant="body2">
                      From: {request.assistant_name}
                    </Typography>
                  )}
                </Alert>

                {helpRequestMessage && (
                  <Box
                    data-testid="manager-help-request"
                    sx={{
                      bgcolor: 'action.hover',
                      borderLeft: 4,
                      borderColor: 'warning.main',
                      p: 2,
                      mb: 2,
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {helpRequestMessage.content}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      {new Date(helpRequestMessage.date_created).toLocaleString()}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setManagerRespondOpen(true)}
                  >
                    Send Note to Assistant
                  </Button>
                </Box>
              </CardContent>
            </Card>
          );
        })()}

        {/* Need Help Section - for assistants who claimed the request */}
        {isOpen &&
         authStore.user?.is_beta &&
         request.assistant_uuid === authStore.user?.uuid &&
         authStore.isAssistant && (
          request.needs_manager_attention ? (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Manager help requested. Waiting for a response.
                </Alert>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCancelHelp}
                >
                  Cancel Help Request
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="subtitle2">
                      Need help with this request?
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Contact your manager if you need assistance.
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<HelpOutlineIcon />}
                    onClick={() => setNeedHelpOpen(true)}
                  >
                    Request Help
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )
        )}

        {requestStore.error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {requestStore.error}
          </Alert>
        )}
      </Box>

      {/* Sidebar Toggle Button */}
      <IconButton
        onClick={() => setSidebarOpen(!sidebarOpen)}
        sx={{
          position: 'fixed',
          right: sidebarOpen ? 328 : 8,
          top: 80,
          zIndex: 1000,
          bgcolor: 'background.paper',
          boxShadow: 1,
          '&:hover': { bgcolor: 'grey.100' },
        }}
      >
        {sidebarOpen ? <ChevronRightIcon /> : <ChevronLeftIcon />}
      </IconButton>

      {/* Sidebar */}
      <Collapse in={sidebarOpen} orientation="horizontal">
        <Box
          sx={{
            width: 320,
            flexShrink: 0,
          }}
        >
          <Stack spacing={2}>
            {/* Request Details Card */}
            <RequestDetailsCard
              request={request}
              clientInfo={clientInfo}
              loading={clientInfoLoading}
            />

            {/* Attachments Card */}
            <AttachmentsCard
              requestKey={requestKey || ''}
              requestUuid={request.uuid}
              canWork={canWorkOnRequest(request, authStore.user?.uuid, authStore.isAssistant)}
            />

            {/* Phone Calls Card */}
            <PhoneCallsCard
              requestUuid={request.uuid}
              onOpenDialer={(number) => {
                setDialerInitialNumber(number);
                setPhoneDialerOpen(true);
              }}
              canWork={canWorkOnRequest(request, authStore.user?.uuid, authStore.isAssistant)}
            />

            {/* Email Card */}
            <EmailCard
              requestUuid={request.uuid}
              onOpenComposer={(email) => {
                setReplyToEmail(email);
                setEmailComposerOpen(true);
              }}
              canWork={canWorkOnRequest(request, authStore.user?.uuid, authStore.isAssistant)}
            />

            {/* Calendar Card */}
            <CalendarCard
              requestKey={requestKey || ''}
              canWork={canWorkOnRequest(request, authStore.user?.uuid, authStore.isAssistant)}
              clientUuid={clientInfo?.uuid}
            />

            {/* User Notes Card */}
            {clientInfo?.key && (
              <UserNotesCard
                userKey={clientInfo.key}
                canWork={canWorkOnRequest(request, authStore.user?.uuid, authStore.isAssistant)}
              />
            )}

            {/* Field Guide Suggestions Card */}
            <FieldGuideSuggestionsCard
              requestKey={request.key}
              requestUuid={request.uuid}
            />

            {/* Internal Tools Card */}
            <InternalToolsCard requestKey={request.datastore_key} />

            {/* Mentor History Card - show for assistants */}
            <MentorHistoryCard
              requestKey={requestKey || ''}
              isManager={authStore.roles.includes('manager')}
            />

            {/* Manager Actions Card - only show for managers */}
            {authStore.roles.includes('manager') && authStore.isAssistant && (
              <ManagerActionsCard
                request={request}
                onRequestUpdate={() => requestStore.fetchRequest(requestKey!)}
              />
            )}
          </Stack>
        </Box>
      </Collapse>

      {/* Phone Dialer Drawer */}
      <PhoneDialer
        open={phoneDialerOpen}
        onClose={() => {
          setPhoneDialerOpen(false);
          setDialerInitialNumber(undefined);
        }}
        requestUuid={request.uuid}
        initialNumber={dialerInitialNumber}
      />

      {/* Email Composer Drawer */}
      <EmailComposer
        open={emailComposerOpen}
        onClose={() => {
          setEmailComposerOpen(false);
          setReplyToEmail(undefined);
          setReplyToIncomingEmail(undefined);
        }}
        requestUuid={request.uuid}
        blockedEmail={clientInfo?.primary_email ?? undefined}
        replyToEmail={replyToEmail}
        replyToIncomingEmail={replyToIncomingEmail}
      />

      {/* Need Help Dialog */}
      <Dialog
        open={needHelpOpen}
        onClose={() => !needHelpSending && setNeedHelpOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Contact Your Manager</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
            The client will <strong>not</strong> be able to see this note.
            Use this to ask your manager a question about this request.
          </Alert>
          <TextField
            autoFocus
            margin="dense"
            label="What do you need help with?"
            fullWidth
            multiline
            rows={4}
            value={needHelpMessage}
            onChange={(e) => setNeedHelpMessage(e.target.value)}
            disabled={needHelpSending}
            placeholder="Describe what assistance you need..."
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setNeedHelpOpen(false)}
            disabled={needHelpSending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRequestHelp}
            variant="contained"
            color="warning"
            disabled={!needHelpMessage.trim() || needHelpSending}
          >
            {needHelpSending ? <CircularProgress size={24} /> : 'Send to Manager'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manager Respond Dialog */}
      <Dialog
        open={managerRespondOpen}
        onClose={() => !managerRespondSending && setManagerRespondOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send Note to Assistant</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
            This message will be sent to the assistant and will clear the attention flag.
          </Alert>
          <TextField
            autoFocus
            margin="dense"
            label="Message to assistant"
            fullWidth
            multiline
            rows={4}
            value={managerRespondMessage}
            onChange={(e) => setManagerRespondMessage(e.target.value)}
            disabled={managerRespondSending}
            placeholder="Enter your response..."
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Set request status
            </Typography>
            <Select
              value={managerRespondStatus}
              onChange={(e: SelectChangeEvent<number>) => setManagerRespondStatus(e.target.value as number)}
              disabled={managerRespondSending}
              size="small"
            >
              <MenuItem value={REQUEST_STATUS.OPEN}>Open</MenuItem>
              <MenuItem value={REQUEST_STATUS.AWAITING_RESPONSE}>Awaiting Response</MenuItem>
              <MenuItem value={REQUEST_STATUS.AWAITING_THIRD_PARTY}>Awaiting Third Party</MenuItem>
              <MenuItem value={REQUEST_STATUS.CLOSED}>Closed</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setManagerRespondOpen(false)}
            disabled={managerRespondSending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleManagerRespond}
            variant="contained"
            disabled={!managerRespondMessage.trim() || managerRespondSending}
          >
            {managerRespondSending ? <CircularProgress size={24} /> : 'Send Note'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Close Without Responding Confirmation Dialog */}
      <Dialog
        open={closeTaskOpen}
        onClose={() => !closeTaskSending && setCloseTaskOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Close Without Responding</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            This will submit a request to close the task <strong>without notifying the client</strong>.
            A mentor must approve this action.
          </Alert>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Use this when the client's message doesn't require a response (e.g., "thanks" messages).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCloseTaskOpen(false)}
            disabled={closeTaskSending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCloseTask}
            variant="contained"
            color="warning"
            disabled={closeTaskSending}
          >
            {closeTaskSending ? <CircularProgress size={24} /> : 'Yes, Close Without Responding'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Release Task Confirmation Dialog */}
      <Dialog
        open={releaseOpen}
        onClose={() => !releasing && setReleaseOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Release Task</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 1 }}>
            "{request.title}" will be unassigned from {authStore.user?.first_name || authStore.user?.name || 'this account'} and
            go back to the Available queue so another assistant can claim it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setReleaseOpen(false)}
            disabled={releasing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRelease}
            variant="contained"
            disabled={releasing}
          >
            {releasing ? <CircularProgress size={24} /> : 'Yes, Release Task'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

// Helper to get human-readable status label
const getStatusLabel = (status: number | null): string => {
  if (status === null) return 'Unknown';
  switch (status) {
    case REQUEST_STATUS.NEW: return 'New';
    case REQUEST_STATUS.OPEN: return 'Open';
    case REQUEST_STATUS.CLOSED: return 'Closed';
    case REQUEST_STATUS.AWAITING_THIRD_PARTY: return 'Waiting on Third Party';
    case REQUEST_STATUS.AWAITING_MENTOR_APPROVAL: return 'Awaiting Mentor Approval';
    case REQUEST_STATUS.MENTOR_RESPONSE: return 'Mentor Response';
    case REQUEST_STATUS.CLIENT_RESPONSE: return 'Client Response';
    case REQUEST_STATUS.EXPIRED: return 'Expired';
    case REQUEST_STATUS.ARCHIVED: return 'Archived';
    default: return `Status ${status}`;
  }
};

// Build message actions - same for all users, all go through mentor queue
const getMessageActions = (requestStatus?: number): { value: MessageAction; label: string; suggestedStatus: number }[] => {
  // Don't show options if already awaiting mentor approval
  const awaitingReview = requestStatus === REQUEST_STATUS.AWAITING_MENTOR_APPROVAL;

  if (awaitingReview) {
    return [];
  }

  return [
    { value: 'open', label: 'Keep Open', suggestedStatus: REQUEST_STATUS.OPEN },
    { value: 'awaiting', label: 'Awaiting Response', suggestedStatus: REQUEST_STATUS.AWAITING_RESPONSE },
    { value: 'close', label: 'Close', suggestedStatus: REQUEST_STATUS.CLOSED },
    { value: 'waiting', label: 'Waiting on third party', suggestedStatus: REQUEST_STATUS.AWAITING_THIRD_PARTY },
  ];
};

// Main exported component with context providers
export const AssistantRequestDetail = observer(() => {
  const { requestKey } = useParams<{ requestKey: string }>();
  // Key by request so navigating between requests (e.g. mentor "next task")
  // remounts and resets all per-request state (clientInfo, drafts, etc.)
  return (
    <PhoneProvider>
      <EmailProvider>
        <AssistantRequestDetailInner key={requestKey} />
      </EmailProvider>
    </PhoneProvider>
  );
});
